// Layout tab — the data source's columns as a tree, one folder per table /
// schema folder (SQL mode: one "Query columns" folder). Tick a field to add it
// (Auto picks where, and the visual type when Auto is on); untick to remove;
// drag it onto a drop zone or onto the chart. Dropping a placed column back on
// the tree removes it from the visual.
//
// The visual's own CALCULATIONS (calculations.ts) are a folder of their own,
// first: "ƒx New calculation" under the search box, a pencil on each one, and
// a calculation that cannot run is shown in red with the reason.

import { useMemo, useState } from 'react'
import { AlertCircle, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Pencil, Search, Table2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { groupByTable, type BankColumn } from './dataSource'
import { roleOf } from './autoVisual'
import { CALC_FOLDER } from './calculations'

export function fieldGlyph(b: BankColumn): { text: string; cls: string; title: string } {
  if (b.calcKey) return { text: 'ƒx', cls: 'vdo-g-calc', title: 'Calculation' }
  const role = roleOf(b)
  if (role === 'measure') return { text: '#', cls: 'vdo-g-num', title: 'Number / measure' }
  if (role === 'date') return { text: '31', cls: 'vdo-g-date', title: 'Date' }
  if (role === 'geo') return { text: '⌖', cls: 'vdo-g-geo', title: 'Location' }
  return { text: 'Aa', cls: 'vdo-g-dim', title: 'Text / dimension' }
}

export default function FieldTree({
  bank, mode, placedIn, readOnly, emptyHint, headerExtra,
  onToggle, onDragStart, onDragEnd, canDropBack, onDropBack,
  onNewCalc, onEditCalc, calcUnavailable,
}: {
  bank: BankColumn[]
  mode: string
  /** bank key → the drop zone name it sits in (absent = not on the visual). */
  placedIn: Map<string, string>
  readOnly: boolean
  /** Shown instead of the tree when there is nothing to list. */
  emptyHint?: string
  headerExtra?: ReactNode
  onToggle: (key: string, add: boolean) => void
  onDragStart: (key: string) => void
  onDragEnd: () => void
  canDropBack: boolean
  onDropBack: () => void
  /** Open the calculation editor for a new calculation (absent = no button). */
  onNewCalc?: () => void
  onEditCalc?: (calcKey: string) => void
  /** Why calculations can't be made on this data source ('' = they can). */
  calcUnavailable?: string
}) {
  const [q, setQ] = useState('')
  const groups = useMemo(() => {
    if (mode === 'sqlmodel') return groupByTable(bank)
    return bank.length ? [{ table: 'Query columns', columns: bank }] : []
  }, [mode, bank])
  // Folders open by default; the first folder never starts closed.
  const [closed, setClosed] = useState<Record<string, boolean>>({})
  const term = q.trim().toLowerCase()
  const visible = groups
    .map((g) => ({ ...g, columns: g.columns.filter((c) => !term || (c.label || c.name).toLowerCase().includes(term)) }))
    .filter((g) => g.columns.length)

  return (
    <div
      style={{ minHeight: '100%' }}
      onDragOver={(e) => { if (canDropBack) e.preventDefault() }}
      onDrop={(e) => { if (canDropBack) { e.preventDefault(); onDropBack() } }}
    >
      <div className="vdo-colh">
        Fields <span className="s">{bank.length}</span>
        <span style={{ flex: 1 }} />
        {headerExtra}
        {groups.length > 1 && (
          <>
            <button className="vdo-ib sm" title="Expand all" aria-label="Expand all folders"
              onClick={() => setClosed({})}><ChevronsUpDown size={15} /></button>
            <button className="vdo-ib sm" title="Collapse all" aria-label="Collapse all folders"
              onClick={() => setClosed(Object.fromEntries(groups.map((g) => [g.table, true])))}><ChevronsDownUp size={15} /></button>
          </>
        )}
      </div>
      <label className="vdo-search">
        <Search size={15} />
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${bank.length} fields`} aria-label="Search fields" />
      </label>
      {onNewCalc && !readOnly && (
        <button type="button" className="vdo-newcalc" onClick={onNewCalc} disabled={!!calcUnavailable}
          title={calcUnavailable || 'Make a field from a formula over the others — e.g. Margin % = Gross margin ÷ Net sales × 100'}>
          <span className="vdo-glyph vdo-g-calc" aria-hidden="true">ƒx</span>
          New calculation
          <span className="s">{calcUnavailable ? 'schema only' : 'from your fields'}</span>
        </button>
      )}
      {emptyHint && !bank.length && (
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '8px 8px' }}>{emptyHint}</p>
      )}
      {visible.map((g) => {
        const open = !!term || !closed[g.table]
        return (
          <div key={g.table}>
            <button className="vdo-fold" aria-expanded={open}
              onClick={() => setClosed((c) => ({ ...c, [g.table]: !c[g.table] }))}>
              {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              {g.table === CALC_FOLDER && g.columns.every((c) => c.calcKey)
                ? <span className="vdo-glyph vdo-g-calc" style={{ flex: '0 0 20px', height: 16, fontSize: 9 }} aria-hidden="true">ƒx</span>
                : <Table2 size={14} />}
              <span className="nm">{g.table}</span>
              <span style={{ marginLeft: 'auto', color: 'var(--color-text-faint)' }}>{g.columns.length}</span>
            </button>
            {open && [...g.columns]
              .sort((a, b) => (roleOf(a) === 'measure' ? 1 : 0) - (roleOf(b) === 'measure' ? 1 : 0))
              .map((c) => {
                const where = placedIn.get(c.key)
                const gl = fieldGlyph(c)
                const problem = c.calcKey ? c.calcProblem || '' : ''
                return (
                  <div
                    key={c.key}
                    className={`vdo-leaf${where ? ' on' : ''}${problem ? ' bad' : ''}`}
                    draggable={!readOnly}
                    onDragStart={(e) => { e.dataTransfer.effectAllowed = 'copyMove'; e.dataTransfer.setData('text/plain', c.key); onDragStart(c.key) }}
                    onDragEnd={onDragEnd}
                    title={c.calcKey
                      ? `${c.label} = ${c.calcFormula || ''}${problem ? `\n⚠ ${problem}` : ''}`
                      : c.DBTABLE ? `${c.DBTABLE}.${c.DBNAME}` : c.name}
                  >
                    <input
                      type="checkbox"
                      checked={!!where}
                      disabled={readOnly}
                      onChange={(e) => onToggle(c.key, e.target.checked)}
                      aria-label={`Use ${c.label || c.name} in the visual`}
                    />
                    <span className={`vdo-glyph ${gl.cls}`} title={gl.title}>{gl.text}</span>
                    <span className="lab">{c.label || c.name}</span>
                    {problem && (
                      <span className="vdo-leaf-bad" title={problem} role="img" aria-label={`Problem: ${problem}`}>
                        <AlertCircle size={14} />
                      </span>
                    )}
                    {where && <span className="vdo-where" title={`In ${where}`}>{where}</span>}
                    {c.calcKey && onEditCalc && (
                      <button type="button" className="vdo-ib sm vdo-leaf-ed" title={readOnly ? 'View calculation' : 'Edit calculation'}
                        aria-label={`${readOnly ? 'View' : 'Edit'} the calculation ${c.label}`}
                        onClick={() => onEditCalc(c.calcKey!)}>
                        <Pencil size={13} />
                      </button>
                    )}
                  </div>
                )
              })}
          </div>
        )
      })}
      {!!term && !visible.length && (
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '8px 8px' }}>No field matches “{q}”.</p>
      )}
    </div>
  )
}
